/**
 * Main logic for the cookie interval listener
 */
function listenerLoop() {
  let newCookieValue;
  for (const cookie in this.cookiesToListen) {
    /* istanbul ignore else  */
    if (this.cookiesToListen.hasOwnProperty(cookie)) {
      newCookieValue = this.get(cookie);
      // We have to give it a value for the null cases in order to detect cookies that get created as well, if not
      // the first value after the creation will just get assigned and won't trigger the associated function
      newCookieValue = this.get(cookie) || this.NULL_UNIQUE_KEY;

      if (this.cookieRegistry[cookie]) {
        /* istanbul ignore else  */
        if (newCookieValue !== this.cookieRegistry[cookie]) {
          // update value
          this.cookieRegistry[cookie] = newCookieValue;
          // execute callback
          if (newCookieValue === this.NULL_UNIQUE_KEY) {
            this.cookiesToListen[cookie](null);
          } else {
            this.cookiesToListen[cookie](newCookieValue);
          }
        }
      } else {
        this.cookieRegistry[cookie] = newCookieValue;
      }
    }
  }
}

/**
 * Cookie functions - set, get and eat
 */
export class Cookie {
  constructor() {
    this.listenerInterval = undefined;
    this.cookiesToListen = {};
    this.cookieRegistry = {};
    this.NULL_UNIQUE_KEY = '_-+*=NuLL=*+-_';
    this.delay = 3000;
  }

  /**
   * Set a cookie
   *
   * @param {String} name Cookie name string
   * @param {String} val value string
   * @param {Number} day optional number of days for cookie to exist
   * @param {Boolean} dom Domain
   * @return {void}
   */
  set(name, val, day, dom) {
    let exp = '';
    const domain = dom ? ';domain=.ig.com' : '';

    /* istanbul ignore else  */
    if (day) {
      const date = new Date();
      date.setTime(date.getTime() + day * 24 * 60 * 60 * 1000);
      exp = `; expires=${date.toGMTString()}`;
    }

    document.cookie = `${name}=${val + exp + domain}; path=/`;

    /* istanbul ignore else  */
    if (typeof window.dlog !== 'undefined' && !!window.dlog) {
      window.dlog(
        `Cookie: setting cookie with value: ${name}= ${val +
          exp +
          domain}; path=/ from: `,
        arguments
      );
    }
  }

  /**
   * Get a cookie
   *
   * @param {String} name Cookie name string
   * @return {String} The value of the cookie
   */
  get(name) {
    const nEQ = `${name}=`;
    const cookiesArray = document.cookie.split(';');

    for (let i = 0; i < cookiesArray.length; i += 1) {
      const cookie = cookiesArray[i].trim();

      /* istanbul ignore else  */
      if (cookie.indexOf(nEQ) === 0) {
        return cookie.substring(nEQ.length, cookie.length);
      }
    }

    return null;
  }

  /**
   * Eat a cookie
   *
   * @param {String} name Cookie name string
   * @param {String} dom Cookie domain
   * @return {void}
   */
  eat(name, dom) {
    this.set(name, '', -1, dom);
  }

  /**
   * Listen to cookie changes
   * @param cookieName -> Cookie name
   * @param callbackFunction -> function to execute on cookie change. It will be passed the new cookie value
   * @param interval -> interval value, it defaults to 3000
   */
  listen(cookieName, callbackFunction, interval) {
    this.cookiesToListen[cookieName] = callbackFunction;
    // ensure we just create one global interval listener
    /* istanbul ignore else  */
    if (this.listenerInterval === undefined) {
      this.listenerInterval = setInterval(
        listenerLoop.bind(this),
        interval || this.delay
      );
    }
  }

  /**
   * Remove cookie listener
   * @param cookieName -> Cookie name
   */
  removeListener(cookieName) {
    /* istanbul ignore else  */
    if (this.cookiesToListen.hasOwnProperty(cookieName)) {
      delete this.cookiesToListen[cookieName];

      /* istanbul ignore else  */
      if (Object.keys(this.cookiesToListen).length === 0) {
        clearInterval(this.listenerInterval);
        this.listenerInterval = undefined;
      }
    }
  }
}
